/**
 * Transforma el objeto `standings` del context al formato que consume GroupTable.
 *
 * @param {Object|null|undefined} standings - El state completo de standings del context.
 * @param {Object}   standings.groupStandings
 * @param {Array}    standings.groupStandings.groups
 * @param {string}   standings.groupStandings.groups[].groupCode
 * @param {string}   standings.groupStandings.groups[].groupName
 * @param {Array}    standings.groupStandings.groups[].teams
 * @param {string}   standings.groupStandings.groups[].teams[].team.flagUrl
 * @param {Object}   standings.columns
 * @param {Array}    standings.columns.standings - Array de { key, label } para los headers de la tabla
 *
 * @returns {Array<{id: string, name: string, teams: Array<{rank: number, name: string, code: string, flag: string, stats: Object}>}>}
 */
/**
 * Extrae los headers de la tabla desde columns.standings.
 *
 * @param {Object|null|undefined} standings - El state completo de standings del context.
 * @returns {{ fixed: Array<{key: string, label: string}>, stats: Array<{key: string, label: string}> }}
 */
export const getStandingsColumns = (standings) => {
  const columns = standings?.columns?.standings;

  if (!Array.isArray(columns) || columns.length === 0) {
    return { fixed: [], stats: [] };
  }

  const fixed = columns.filter(
    (col) => col.key === 'rank' || col.key === 'team.name'
  );
  const stats = columns.filter(
    (col) => col.key !== 'rank' && col.key !== 'team.name'
  );

  return { fixed, stats };
};

/**
 * Transforma el ranking del context al formato que consume GroupTable.
 *
 * @param {Object|null|undefined} standings - El state completo de standings del context.
 * @param {Object}   standings.ranking
 * @param {string}   standings.ranking.label
 * @param {Array}    standings.ranking.teams
 *
 * @returns {{ title: string, rows: Array<{rank: number, name: string, code: string, flag: string, stats: Object}> }}
 */
export const transformStandingsToRanking = (standings) => {
  const ranking = standings?.ranking;

  if (!ranking || !Array.isArray(ranking.teams)) {
    return { title: '', rows: [] };
  }

  return {
    title: ranking.label ?? '',
    rows: ranking.teams.map((entry) => ({
      id: entry.team?.id,
      rank: entry.rank,
      name: entry.team?.name ?? '',
      code: entry.team?.countryCode ?? entry.team?.shortName ?? '',
      flag: entry.team?.flagUrl ?? '',
      stats: entry.stats ?? {},
    })),
  };
};

// ─── Bracket / Tournament ────────────────────────────────────────────────────

const STATUS_MAP = {
  SCHEDULED: 'pending',
  PENDING_PARTICIPANTS: 'pending',
  LIVE: 'live',
  IN_PROGRESS: 'live',
  COMPLETED: 'finished',
  FINISHED: 'finished',
  FINAL: 'finished',
  POSTPONED: 'postponed',
};

const mapStatus = (apiStatus) => {
  if (!apiStatus) return 'pending';
  return STATUS_MAP[apiStatus] ?? apiStatus.toLowerCase();
};

const parseDisplayClock = (displayClock) => {
  if (!displayClock) return null;
  const segments = displayClock.match(/(\d+)/g);
  if (!segments || segments.length === 0) return null;
  return {
    minute: segments[0],
    extraMinute: segments[1] ?? null,
    stoppageMinute: segments[2] ?? null,
  };
};

const transformProgress = (match, mappedStatus) => {
  if (mappedStatus !== 'live') return null;
  const mp = match.matchProgress;
  if (!mp) return null;

  const clock = parseDisplayClock(mp.displayClock);
  return {
    period: mp.period ?? null,
    minute: clock?.minute ?? mp.minute ?? '0',
    extraMinute: clock?.extraMinute ?? null,
    stoppageMinute: clock?.stoppageMinute ?? null,
  };
};

const transformSide = (side, score) => ({
  id: side?.team?.id ?? null,
  name: side?.team?.name ?? side?.label ?? 'Por definir',
  code: side?.team?.countryCode ?? side?.team?.shortName ?? null,
  flag: side?.team?.flagUrl ?? null,
  score: score ?? null,
  sourceType: side?.sourceType
});

const transformMatch = (match) => {
  const status = mapStatus(match.status);
  return {
    id: match.matchId,
    date: match.displayDate ?? '',
    time: match.displayTime ?? '',
    timezone: match.timeZoneLabel ?? '',
    status,
    homeTeam: transformSide(match.homeSide, match.score?.home),
    awayTeam: transformSide(match.awaySide, match.score?.away),
    isBroadcastAvailable: match.broadcast?.isAvailable ?? false,
    progress: transformProgress(match, status),
  };
};

/**
 * Transforma el bracket del context al formato que consume TournamentView.
 *
 * - Filtra THIRD_PLACE de los rounds; sus matches se fusionan dentro de FINAL.
 * - IDs de ronda = code en lowercase (ej. QUARTER_FINALS → 'quarter_finals').
 * - Labels de ronda vienen de filters.availableBracketRounds[].name.
 *
 * Cada match tiene el shape:
 * { id, date, time, timezone, status,
 *   homeTeam: { id: string|null, name, code: string|null, flag, score },
 *   awayTeam: { id: string|null, name, code: string|null, flag, score },
 *   isBroadcastAvailable: boolean,
 *   progress: { period: string|null, minute: string, extraMinute: string|null, stoppageMinute: string|null } | null }
 *
 * @param {Object|null|undefined} standings - El state completo de standings del context.
 * @returns {{ rounds: Array<{id: string, label: string, matches: Array}> }}
 */
export const transformBracketTournament = (standings) => {
  const availableRounds = standings?.filters?.availableBracketRounds;
  const bracketRounds = standings?.bracket?.rounds;

  if (!Array.isArray(availableRounds) || !Array.isArray(bracketRounds)) {
    return { rounds: [] };
  }

  const bracketByCode = {};
  bracketRounds.forEach((r) => {
    bracketByCode[r.code] = r;
  });

  const filteredRounds = availableRounds.filter((r) => r.code !== 'THIRD_PLACE');

  return {
    rounds: filteredRounds.map((round) => {
      const code = round.code;
      const bracketRound = bracketByCode[code];
      let matches = Array.isArray(bracketRound?.matches)
        ? bracketRound.matches.map(transformMatch)
        : [];

      if (code === 'FINAL') {
        const thirdPlace = bracketByCode['THIRD_PLACE'];
        if (Array.isArray(thirdPlace?.matches)) {
          matches = [...matches, ...thirdPlace.matches.map(transformMatch)];
        }
      }

      return {
        id: code.toLowerCase(),
        label: round.name,
        matches,
      };
    }),
  };
};

// ─── Group Standings ─────────────────────────────────────────────────────────

export const transformStandingsToGroups = (standings) => {
  const groups = standings?.groupStandings?.groups;

  if (!Array.isArray(groups)) return [];

  return groups.map((group) => ({
    id: group.groupCode,
    name: group.groupName,
    teams: Array.isArray(group.teams)
      ? group.teams.map((entry) => ({
        id: entry.team.id,
        rank: entry.rank,
        name: entry.team?.name ?? '',
        code: entry.team?.countryCode ?? entry.team?.shortName ?? '',
        flag: entry.team?.flagUrl ?? '',
        stats: entry.stats ?? {},
      }))
      : [],
  }));
};
